import { TypedDocumentNode, gql } from '@apollo/client';
import qs from 'qs';
import { useIntl } from 'react-intl';
import { Link, generatePath, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { FootballRivalsInviteStatus } from '@sorare/core/src/__generated__/globalTypes';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import Bold from '@sorare/core/src/atoms/typography/Bold';
import {
  FOOTBALL_PLAY_RIVALS_DRAFT_SLUG,
  FOOTBALL_PLAY_RIVALS_GAMES_ID,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { rivals } from '@sorare/core/src/lib/glossary';

import { CompetitionName } from '@sorare/gameplay/src/components/game/Game/CompetitionName';
import { GameTeamsMatchup } from '@sorare/gameplay/src/components/game/Game/GameTeamsMatchup';
import InlineGame from '@sorare/gameplay/src/components/stats/InlineGame';

import { MyAvailableCards } from 'components/rivals/RivalsMatchesDateLayout/Game/TopRightArea/MyAvailableCards';
import { GameWrapper } from 'components/rivals/RivalsMatchesDateLayout/GameWrapper';
import useFootballRivalsInviteAccept from 'components/rivals/matchups/useFootballRivalsInviteAccept';
import useFootballRivalsInviteCreate from 'components/rivals/matchups/useFootballRivalsInviteCreate';
import useFootballRivalsInviteReject from 'components/rivals/matchups/useFootballRivalsInviteReject';

import { RivalryGameFooter } from './RivalryGameFooter';
import {
  RivalryGame_footballRivalsGame,
  RivalryGame_footballRivalsManager,
} from './__generated__/index.graphql';

const SpaceBetweenHorizontal = styled(SBHorizontal)`
  padding: var(--double-unit);
  min-height: var(--triple-unit);
`;
const StyledLink = styled(Link)`
  display: inline-flex;
  gap: var(--unit);
  align-items: center;
  font-weight: bold;
`;

type Props = {
  footballRivalsGame: RivalryGame_footballRivalsGame;
  footballRivalsManager: RivalryGame_footballRivalsManager;
  selectedDate?: string;
  onCreateInvite: () => void;
};
export const RivalryGame = ({
  footballRivalsGame,
  footballRivalsManager,
  selectedDate,
  onCreateInvite,
}: Props) => {
  const { myLineup, game, myInvite, slug } = footballRivalsGame;
  const { formatMessage } = useIntl();
  const { currentUser } = useCurrentUserContext();
  const acceptInvite = useFootballRivalsInviteAccept();
  const createInvite = useFootballRivalsInviteCreate();
  const rejectInvite = useFootballRivalsInviteReject();
  const navigate = useNavigate();
  const { showNotification } = useSnackNotificationContext();

  const matchedUp = myInvite?.status === FootballRivalsInviteStatus.ACCEPTED;
  const inviteCreated = myInvite?.status === FootballRivalsInviteStatus.CREATED;
  const inviteReceived =
    inviteCreated && myInvite?.invitedBy?.user.slug !== currentUser?.slug;
  const inviteSent = inviteCreated && !inviteReceived;
  const inviteRejected =
    myInvite?.status === FootballRivalsInviteStatus.REFUSED;
  const inviteRejectedByOpponent =
    inviteRejected && myInvite.invitedBy.user.slug === currentUser?.slug;
  const inviteRejectedByMe = inviteRejected && !inviteRejectedByOpponent;

  const onAcceptInvite = async (inviteId: string) => {
    if (myLineup) {
      await acceptInvite({ inviteId });
    } else {
      navigate(
        `${generatePath(FOOTBALL_PLAY_RIVALS_DRAFT_SLUG, {
          slug,
        })}${qs.stringify(
          {
            invitesToAccept: [inviteId],
            rival: footballRivalsManager.user.slug,
            rivalryDate: selectedDate,
          },
          { addQueryPrefix: true }
        )}`
      );
    }
  };

  const onGameClick = async () => {
    if (matchedUp || inviteSent || inviteRejectedByOpponent) {
      showNotification('rivalsInvitationAlreadySent', {
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      });
    } else if (inviteReceived) {
      onAcceptInvite(myInvite.id);
    } else if (myLineup) {
      showNotification('rivalsInvitationSent', {
        b: Bold,
        nickname: footballRivalsManager.user.nickname,
        game: <InlineGame inline game={footballRivalsGame.game} />,
        link: (...chunks: string[]) => (
          <StyledLink
            to={generatePath(FOOTBALL_PLAY_RIVALS_GAMES_ID, {
              id: footballRivalsGame.id,
            })}
          >
            {chunks}
          </StyledLink>
        ),
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      });
      await createInvite({
        gameId: footballRivalsGame.id,
        managerId: footballRivalsManager.id,
      });
      onCreateInvite();
    } else {
      navigate(
        `${generatePath(FOOTBALL_PLAY_RIVALS_DRAFT_SLUG, {
          slug,
        })}${qs.stringify(
          {
            invitesToCreate: [footballRivalsManager.id],
            rival: footballRivalsManager.user.slug,
            rivalryDate: selectedDate,
          },
          { addQueryPrefix: true }
        )}`
      );
    }
  };

  return (
    <LinkBox>
      <GameWrapper
        labelProps={
          myLineup
            ? {
                value: formatMessage(rivals.joined),
                bgColor: 'var(--c-rivals-friendlies)',
                color: 'var(--c-neutral-100)',
              }
            : undefined
        }
      >
        <Vertical>
          <SpaceBetweenHorizontal gap={1}>
            <CompetitionName displayName={game.competition.displayName} />
            {!myLineup && (
              <MyAvailableCards footballRivalsGame={footballRivalsGame} />
            )}
          </SpaceBetweenHorizontal>
          <LinkOverlay as={ButtonBase} onClick={onGameClick}>
            <GameTeamsMatchup game={footballRivalsGame.game} />
          </LinkOverlay>
          {!!myInvite && (
            <RivalryGameFooter
              inviteReceived={inviteReceived}
              inviteRejectedByMe={inviteRejectedByMe}
              inviteRejectedByOpponent={inviteRejectedByOpponent}
              matchedUp={matchedUp}
              user={myInvite.invitedBy.user}
              onAcceptReceivedInvite={() => {
                onAcceptInvite(myInvite.id);
              }}
              onRejectReceivedInvite={async () => {
                await rejectInvite({ inviteId: myInvite.id });
              }}
            />
          )}
        </Vertical>
      </GameWrapper>
    </LinkBox>
  );
};

RivalryGame.fragments = {
  footballRivalsGame: gql`
    fragment RivalryGame_footballRivalsGame on FootballRivalsGame {
      slug
      id
      myLineup {
        id
      }
      game {
        id
        competition {
          slug
          id
          displayName
        }
        ...InlineGame_game
        ...GameTeamsMatchup_anyGameInterface
      }
      myInvite(userSlug: $userSlug) {
        id
        status
        invitedBy {
          id
          user {
            slug
            ...RivalryGameFooter_user
          }
        }
      }
      ...MyAvailableCards_footballRivalsGame
    }
    ${GameTeamsMatchup.fragments.anyGameInterface}
    ${MyAvailableCards.fragments.footballRivalsGame}
    ${RivalryGameFooter.fragments.user}
    ${InlineGame.fragments.game}
  ` as TypedDocumentNode<RivalryGame_footballRivalsGame>,
  footballRivalsManager: gql`
    fragment RivalryGame_footballRivalsManager on FootballRivalsManager {
      id
      user {
        slug
        nickname
      }
    }
  ` as TypedDocumentNode<RivalryGame_footballRivalsManager>,
};
